import React, { useState, useEffect } from "react";
import confetti from "canvas-confetti";
import { AnimatedLogo } from "./ui/animated-logo";

const Preloader = ({ onComplete }) => {
  const [phase, setPhase] = useState("drawing"); // drawing -> text -> revealing -> done
  const [fadeOut, setFadeOut] = useState(false);

  useEffect(() => {
    // Phase 1: Blueprint drawing completes (0 - 1200ms)
    // Phase 2: Text appears (1800ms)
    const textTimer = setTimeout(() => {
      setPhase("text");
    }, 1800);

    // Phase 3: Full reveal with tagline (3000ms)
    const revealTimer = setTimeout(() => {
      setPhase("revealing");
    }, 3000);

    // Phase 4: Start fade out + confetti burst (5000ms)
    const fadeTimer = setTimeout(() => {
      setFadeOut(true);

      // Super awesome confetti celebration!
      const brandColors = ["#cf5e29", "#e8a04a", "#ffffff", "#22263e"];
      const end = Date.now() + 1800; // Longer duration

      // 1. Massive initial burst from the center-bottom
      confetti({
        particleCount: 150,
        spread: 120,
        startVelocity: 80,
        origin: { x: 0.5, y: 1 },
        colors: brandColors,
        zIndex: 100000,
      });

      // 2. Continuous cannons from both bottom corners covering the whole page
      const frame = () => {
        confetti({
          particleCount: 8,
          angle: 60,
          spread: 120,
          startVelocity: 70, // Shoot higher to cover screen
          origin: { x: -0.05, y: 1.05 }, // Start slightly off-screen
          colors: brandColors,
          zIndex: 100000,
        });
        confetti({
          particleCount: 8,
          angle: 120,
          spread: 120,
          startVelocity: 70, // Shoot higher to cover screen
          origin: { x: 1.05, y: 1.05 }, // Start slightly off-screen
          colors: brandColors,
          zIndex: 100000,
        });

        if (Date.now() < end) {
          requestAnimationFrame(frame);
        }
      };
      frame();
    }, 5000);

    // Phase 5: Complete and remove (5600ms)
    const completeTimer = setTimeout(() => {
      setPhase("done");
      if (onComplete) onComplete();
    }, 5600);

    return () => {
      clearTimeout(textTimer);
      clearTimeout(revealTimer);
      clearTimeout(fadeTimer);
      clearTimeout(completeTimer);
    };
  }, [onComplete]);

  if (phase === "done") return null;

  return (
    <div
      className={`preloader-overlay ${fadeOut ? "preloader-exit" : ""}`}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 99999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        background: "#1a1f35",
        overflow: "hidden",
      }}
    >
      {/* Blueprint Grid Background */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `
            linear-gradient(rgba(100, 140, 200, 0.08) 1px, transparent 1px),
            linear-gradient(90deg, rgba(100, 140, 200, 0.08) 1px, transparent 1px),
            linear-gradient(rgba(100, 140, 200, 0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(100, 140, 200, 0.04) 1px, transparent 1px)
          `,
          backgroundSize: "100px 100px, 100px 100px, 20px 20px, 20px 20px",
          animation: "gridFadeIn 0.6s ease-out forwards",
        }}
      />

      {/* Foreground Content container */}
      <div style={{ position: "relative", zIndex: 10, display: "flex", flexDirection: "column", alignItems: "center" }}>
        
        {/* Animated Logo Container */}
        <div style={{ marginBottom: "32px", display: "flex", justifyContent: "center" }}>
          <AnimatedLogo width={120} />
        </div>

        {/* Company Name */}
        <div className={`preloader-company ${phase === "text" || phase === "revealing" ? "preloader-company-visible" : ""}`}>
          <div style={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: "36px",
            fontWeight: 800,
            color: "white",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            textShadow: "0 4px 12px rgba(0,0,0,0.4)"
          }}>
            Prayosha
          </div>
          <div style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: "14px",
            fontWeight: 400,
            color: "#cf5e29",
            letterSpacing: "0.35em",
            textTransform: "uppercase",
            marginTop: "6px",
            textShadow: "0 2px 8px rgba(0,0,0,0.5)"
          }}>
            Construction
          </div>
        </div>

        {/* Tagline - appears after company name */}
        <div className={`preloader-tagline ${phase === "revealing" ? "preloader-tagline-visible" : ""}`}
          style={{
            marginTop: "24px",
            textAlign: "center",
            maxWidth: "360px",
            padding: "0 24px",
          }}>
          <div style={{
            width: "32px",
            height: "2px",
            background: "#cf5e29",
            margin: "0 auto 16px",
            borderRadius: "1px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.3)"
          }} />
          <p style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: "15px",
            fontWeight: 400,
            color: "rgba(255, 255, 255, 0.8)",
            lineHeight: "1.7",
            letterSpacing: "0.02em",
            margin: 0,
            textShadow: "0 2px 6px rgba(0,0,0,0.4)"
          }}>
            Building with Integrity.<br />Delivering Beyond Expectations.
          </p>
          <div style={{
            marginTop: "20px",
            fontFamily: "monospace",
            fontSize: "11px",
            color: "rgba(200, 220, 255, 0.5)",
            letterSpacing: "0.15em",
            textTransform: "uppercase",
          }}>
            Est. 1995 &nbsp;·&nbsp; 30+ Years of Trust
          </div>
        </div>
      </div>

      {/* Loading bar */}
      <div style={{
        position: "absolute",
        bottom: "60px",
        left: "50%",
        transform: "translateX(-50%)",
        width: "200px",
        textAlign: "center",
        zIndex: 10,
      }}>
        <div style={{
          width: "100%",
          height: "2px",
          background: "rgba(200, 220, 255, 0.1)",
          borderRadius: "1px",
          overflow: "hidden",
        }}>
          <div className="preloader-progress" />
        </div>
        <div style={{
          marginTop: "12px",
          fontFamily: "monospace",
          fontSize: "10px",
          color: "rgba(200, 220, 255, 0.4)",
          letterSpacing: "0.2em",
          textTransform: "uppercase",
        }}>
          Building your experience...
        </div>
      </div>

      {/* Blueprint corner marks */}
      <div className="preloader-corner preloader-corner-tl" />
      <div className="preloader-corner preloader-corner-tr" />
      <div className="preloader-corner preloader-corner-bl" />
      <div className="preloader-corner preloader-corner-br" />

      <style>{`
        @keyframes gridFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        /* Floor plan path drawing animations */
        .preloader-draw-large {
          stroke-dasharray: 4000;
          stroke-dashoffset: 4000;
          animation: drawPathLarge 3.5s cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
        }
        .preloader-draw-medium {
          stroke-dasharray: 2000;
          stroke-dashoffset: 2000;
          animation: drawPathMedium 2.8s cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
        }
        .preloader-draw-small {
          stroke-dasharray: 500;
          stroke-dashoffset: 500;
          animation: drawPathSmall 2s cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
        }

        @keyframes drawPathLarge { to { stroke-dashoffset: 0; } }
        @keyframes drawPathMedium { to { stroke-dashoffset: 0; } }
        @keyframes drawPathSmall { to { stroke-dashoffset: 0; } }

        /* Delay utilities - Scaled up for slow motion */
        .delay-100 { animation-delay: 200ms; }
        .delay-200 { animation-delay: 400ms; }
        .delay-300 { animation-delay: 600ms; }
        .delay-400 { animation-delay: 800ms; }
        .delay-500 { animation-delay: 1000ms; }

        /* Line drawing animations */
        .preloader-line { opacity: 0; }
        .preloader-line-1 { animation: lineFadeIn 0.5s ease-out 0s forwards; }
        .preloader-line-2 { animation: lineFadeIn 0.5s ease-out 0.4s forwards; }
        .preloader-line-3 { animation: lineFadeIn 0.5s ease-out 0.6s forwards; }
        .preloader-line-4 { animation: lineFadeIn 0.5s ease-out 0.8s forwards; }
        .preloader-line-5 { animation: lineFadeIn 0.5s ease-out 1.0s forwards; }

        @keyframes lineFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .preloader-text-dim {
          opacity: 0;
          animation: lineFadeIn 0.4s ease-out forwards;
        }

        /* Company name reveal */
        .preloader-company {
          opacity: 0;
          transform: translateY(12px) scale(0.95);
          transition: all 0.7s cubic-bezier(0.16, 1, 0.3, 1);
          text-align: center;
        }
        .preloader-company-visible {
          opacity: 1;
          transform: translateY(0) scale(1);
        }

        /* Tagline reveal */
        .preloader-tagline {
          opacity: 0;
          transform: translateY(10px);
          transition: all 0.7s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .preloader-tagline-visible {
          opacity: 1;
          transform: translateY(0);
        }

        /* Loading progress bar */
        .preloader-progress {
          height: 100%;
          background: linear-gradient(90deg, rgba(207, 94, 41, 0.9), rgba(207, 94, 41, 0.5));
          border-radius: 1px;
          animation: progressFill 4.8s ease-in-out forwards;
        }
        @keyframes progressFill {
          from { width: 0%; }
          to { width: 100%; }
        }

        /* Blueprint corner registration marks */
        .preloader-corner {
          position: absolute;
          width: 24px;
          height: 24px;
          opacity: 0;
          animation: lineFadeIn 0.4s ease-out 0.2s forwards;
          z-index: 10;
        }
        .preloader-corner-tl {
          top: 24px; left: 24px;
          border-top: 1.5px solid rgba(200, 220, 255, 0.2);
          border-left: 1.5px solid rgba(200, 220, 255, 0.2);
        }
        .preloader-corner-tr {
          top: 24px; right: 24px;
          border-top: 1.5px solid rgba(200, 220, 255, 0.2);
          border-right: 1.5px solid rgba(200, 220, 255, 0.2);
        }
        .preloader-corner-bl {
          bottom: 24px; left: 24px;
          border-bottom: 1.5px solid rgba(200, 220, 255, 0.2);
          border-left: 1.5px solid rgba(200, 220, 255, 0.2);
        }
        .preloader-corner-br {
          bottom: 24px; right: 24px;
          border-bottom: 1.5px solid rgba(200, 220, 255, 0.2);
          border-right: 1.5px solid rgba(200, 220, 255, 0.2);
        }

        /* Exit animation — slide up */
        .preloader-exit {
          animation: slideUpExit 0.6s cubic-bezier(0.76, 0, 0.24, 1) forwards;
        }
        @keyframes slideUpExit {
          from { transform: translateY(0); opacity: 1; }
          to { transform: translateY(-100%); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default Preloader;
